import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { agent } from "apiClient/api"
import { useSnackbar } from "context/snackbarContext"
import type { ISkippedJourneyHotelApiItem } from "models/axiosModel"
import { readSkippedHotels } from "component/quotation/duplicateQuotationSkippedHotelsUtil"
import {
	buildCloneVersionPayload,
	type IVersionReasonType,
	readVersionReasonTypes,
} from "component/quotation/createVersionReasonUtil"

type Options = {
	quotationId: string
	/** Runs before the clone, e.g. edit mode saving the current step first. */
	beforeCreate?: () => Promise<void>
	/** Opens the new version's edit mode; defaults to navigating to /quotation/new?quotation_id=. */
	onOpenNewVersion?: (newQuotationId: string) => void
}

/**
 * Create new version flow: reason popup → clone-version → the new version's edit mode, with the
 * Hotel Availability Update popup in between when the copy left hotels out.
 */
const useCreateQuotationVersion = ({ quotationId, beforeCreate, onOpenNewVersion }: Options) => {

	const navigate = useNavigate()
	const { showError, showSuccess } = useSnackbar()

	const [isReasonOpen, setIsReasonOpen] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [reasonTypes, setReasonTypes] = useState<IVersionReasonType[]>([])
	const [isLoadingReasons, setIsLoadingReasons] = useState(false)
	const [createdVersion, setCreatedVersion] = useState<{ quotationId: string; skippedHotels: ISkippedJourneyHotelApiItem[] } | null>(null)

	const openNewVersion = (newQuotationId: string) => {
		if (onOpenNewVersion) onOpenNewVersion(newQuotationId)
		else navigate(`/quotation/new?quotation_id=${encodeURIComponent(newQuotationId)}`)
	}

	const openReasonPopup = async () => {
		setIsReasonOpen(true)
		if (reasonTypes.length > 0) return
		setIsLoadingReasons(true)
		try {
			const response = await agent.GenericGlobalAPI.get(true).catch(() => null)
			const types = readVersionReasonTypes(response)
			setReasonTypes(types)
			if (types.length === 0) showError("Could not load the version reasons. Please try again.")
		} finally {
			setIsLoadingReasons(false)
		}
	}

	const closeReasonPopup = () => {
		if (!isSubmitting) setIsReasonOpen(false)
	}

	const createVersion = async (selectedReasonIds: number[], note: string) => {
		if (!quotationId || isSubmitting) return
		setIsSubmitting(true)
		try {
			await beforeCreate?.()
			const response = await agent.QuotationCloneVersionAPI.post(quotationId, buildCloneVersionPayload(selectedReasonIds, note), true)
			const newQuotationId = response?.status ? String(response?.data?.quotation_id || "") : ""
			if (!newQuotationId) {
				const message = response?.error?.message
				showError(typeof message === "string" && message ? message : "Something went wrong!")
				return
			}
			setIsReasonOpen(false)
			showSuccess("New version created successfully.")
			const skippedHotels = readSkippedHotels(response)
			if (skippedHotels.length > 0) {
				setCreatedVersion({ quotationId: newQuotationId, skippedHotels })
				return
			}
			openNewVersion(newQuotationId)
		} catch {
			showError("Something went wrong!")
		} finally {
			setIsSubmitting(false)
		}
	}

	const continueToNewVersion = () => {
		if (!createdVersion) return
		const newQuotationId = createdVersion.quotationId
		setCreatedVersion(null)
		openNewVersion(newQuotationId)
	}

	return {
		isSubmitting,
		openReasonPopup,
		reasonPopup: {
			isOpen: isReasonOpen,
			reasonTypes,
			isLoadingReasons,
			isSubmitting,
			onClose: closeReasonPopup,
			onConfirm: createVersion,
		},
		skippedHotelsPopup: {
			isOpen: Boolean(createdVersion),
			skippedHotels: createdVersion?.skippedHotels ?? [],
			onClose: continueToNewVersion,
			createdMessage: "The new version has been created successfully.",
		},
	}
}

export default useCreateQuotationVersion
